import { ReactNode, useState } from "react";
import { LearningSidebar } from "./LearningSidebar";
import { Button } from "@jambushrushti/components/ui/button";
import { Menu, Bell, Home, Search, User } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@jambushrushti/components/ui/input";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@jambushrushti/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@jambushrushti/components/ui/dropdown-menu";

interface LearningLayoutProps {
  children: ReactNode;
  title?: string;
}

export function LearningLayout({ children, title }: LearningLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAdminAuth();

  const handleLogout = async () => {
    await logout();
    window.location.href = "/auth";
  };

  return (
    <div className="flex min-h-screen w-full bg-background">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <LearningSidebar />
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-foreground/60 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="animate-fade-in absolute left-0 top-0 h-full w-72">
            <LearningSidebar onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex min-h-screen w-full flex-1 flex-col">
        {/* Top Header */}
        <header className="sticky top-0 z-40 border-b border-border bg-card/95 shadow-sm backdrop-blur-sm">
          <div className="flex h-16 items-center justify-between px-4 lg:px-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 hover:bg-primary/10 lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5 text-foreground" />
              </Button>
              {title && (
                <div className="flex items-center gap-3">
                  <div className="hidden h-6 w-1 rounded-full bg-primary sm:block" />
                  <h1 className="font-heading text-lg font-bold text-foreground sm:text-xl">
                    {title}
                  </h1>
                </div>
              )}
            </div>

            {/* Search Bar - Desktop */}
            <div className="mx-8 hidden max-w-md flex-1 items-center md:flex">
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search courses, lectures..."
                  className="border-border bg-muted/50 pl-10 transition-colors focus:bg-background"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {/* Home Link */}
              <Button
                variant="ghost"
                size="icon"
                className="h-10 w-10 hover:bg-primary/10"
                asChild
              >
                <Link to="/">
                  <Home className="h-5 w-5 text-muted-foreground transition-colors hover:text-foreground" />
                </Link>
              </Button>

              {/* Notifications */}
              <Button
                variant="ghost"
                size="icon"
                className="relative h-10 w-10 hover:bg-primary/10"
              >
                <Bell className="h-5 w-5 text-muted-foreground transition-colors hover:text-foreground" />
                <span className="absolute right-2 top-2 h-2 w-2 animate-pulse rounded-full bg-primary" />
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-10 gap-2 px-2 hover:bg-primary/10"
                  >
                    <Avatar className="h-8 w-8 border-2 border-primary/20">
                      <AvatarImage src={user?.avatar} />
                      <AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">
                        {user?.name?.charAt(0).toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden text-sm font-medium text-foreground sm:block">
                      {user?.name?.split(" ")[0] || "User"}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56 border-border bg-card"
                >
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-foreground">
                      {user?.name || "User"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {user?.email}
                    </p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/learning/settings" className="cursor-pointer">
                      <User className="mr-2 h-4 w-4" />
                      Profile Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer text-destructive"
                  >
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto bg-muted/30 p-4 sm:p-6 lg:p-8">
          <div className="animate-fade-in">{children}</div>
        </main>
      </div>
    </div>
  );
}
